import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

// Social media icons as SVG components for cleaner look
const TwitterXIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

// Social links configuration
const SOCIAL_LINKS = [
  {
    name: "X / Twitter",
    handle: "@antler_sn",
    url: "https://twitter.com/antler_sn",
    icon: TwitterXIcon,
    bgColor: "bg-black",
    hoverColor: "hover:bg-zinc-800",
    iconColor: "text-white"
  },
  {
    name: "Facebook",
    handle: "@AntlerSportsNetwork",
    url: "https://facebook.com/AntlerSportsNetwork",
    icon: FacebookIcon,
    bgColor: "bg-[#1877F2]",
    hoverColor: "hover:bg-[#166FE5]",
    iconColor: "text-white"
  },
  {
    name: "Instagram",
    handle: "@antlersportsnet",
    url: "https://instagram.com/antlersportsnet",
    icon: InstagramIcon,
    bgColor: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
    hoverColor: "hover:opacity-90",
    iconColor: "text-white"
  }
];

export const SocialMediaWidget = () => {
  return (
    <section className="py-16 md:py-20 bg-black" data-testid="social-media-widget">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase text-white mb-4 tracking-tight">
            Join The <span className="text-orange-500">Conversation</span>
          </h2>
          <p className="text-zinc-400 text-lg md:text-xl max-w-2xl mx-auto">
            Follow us for live updates, behind-the-scenes content, and exclusive coverage
          </p>
        </motion.div>

        {/* Social Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {SOCIAL_LINKS.map((social, index) => (
            <motion.a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`group relative overflow-hidden ${social.bgColor} ${social.hoverColor} p-8 transition-all duration-300 border-2 border-transparent hover:border-orange-500`}
            >
              {/* Icon */}
              <div className="mb-6">
                <social.icon className={`w-12 h-12 ${social.iconColor} group-hover:scale-110 transition-transform`} />
              </div>

              {/* Text */}
              <h3 className="text-white font-bold text-xl mb-1">
                {social.name}
              </h3>
              <p className="text-white/70 text-sm mb-4">
                {social.handle}
              </p>

              {/* CTA */}
              <div className="flex items-center gap-2 text-orange-500 font-bold uppercase text-sm">
                Follow Now
                <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>

              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/10 transition-colors pointer-events-none" />
            </motion.a>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-4 px-8 py-4 bg-zinc-900 border border-white/10">
            <span className="text-zinc-400 text-sm uppercase tracking-wider">Tag us</span>
            <span className="text-orange-500 font-bold text-lg">#AntlerSportsNetwork</span>
            <span className="text-zinc-400 text-sm uppercase tracking-wider">to get featured</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialMediaWidget;
